import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import SearchSelect from '../components/SearchSelect';
import { dt, num, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useLocationFilter } from '../context/LocationContext';
import { useDataTable, TableToolbar, TablePager } from '../components/DataTable';

const REASONS = ['Damaged', 'Expired', 'Near expiry', 'Quality issue', 'Excess / overstock', 'Wrong item', 'Customer return', 'Other'];
const DISPOSITIONS = [
  { v: 'reprocess', label: 'Reprocess → kitchen' },
  { v: 'waste', label: 'Waste → damaged/expired' },
  { v: 'restock', label: 'Restock → warehouse' },
];
const dispoBadge = (d) => ({ reprocess: 'badge-gold', waste: 'badge-red', restock: 'badge-green' }[d] || 'badge-gray');
const statusBadge = (s) => ({ created: 'badge-blue', closed: 'badge-green', cancelled: 'badge-gray' }[s] || 'badge-gray');
const statusLabel = (s) => ({ created: 'awaiting warehouse', closed: 'processed', cancelled: 'cancelled' }[s] || s);
const defaultDispo = (reason) => (/damag|expir/i.test(reason || '') ? 'waste' : 'reprocess');

// ── Branch raises a return of stock back to the warehouse ──
const RaiseReturnModal = ({ locations, canPickBranch, myLocationId, onClose, onSaved }) => {
  const retailLocs = useMemo(() => locations.filter((l) => ['retail', 'online'].includes(l.type)), [locations]);
  const [branchId, setBranchId] = useState(canPickBranch ? (retailLocs[0]?.id || '') : (myLocationId || ''));
  const [stock, setStock] = useState([]);
  const [rows, setRows] = useState([{ key: 1, sel: '', qty: '', reason: REASONS[0] }]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!branchId) { setStock([]); return; }
    api.get(`/inventory/levels?itemType=product&locationId=${branchId}`)
      .then(({ data }) => setStock((data.levels || []).filter((l) => Number(l.quantity) > 0)))
      .catch(() => setStock([]));
  }, [branchId]);

  const stockKey = (l) => `${l.itemId}:${l.variantId}`;
  const byKey = useMemo(() => Object.fromEntries(stock.map((l) => [stockKey(l), l])), [stock]);
  const optLabel = (l) => `${l.item}${l.variant ? ` — ${l.variant}` : ` (${l.unit || 'kg'})`}`;

  const addRow = () => setRows((r) => [...r, { key: Math.max(0, ...r.map((x) => x.key)) + 1, sel: '', qty: '', reason: REASONS[0] }]);
  const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const delRow = (key) => setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.key !== key) : rs));

  const submit = async () => {
    setError('');
    const items = rows.map((r) => {
      const l = byKey[r.sel];
      return l ? { itemType: 'product', itemId: l.itemId, variantId: l.variantId, qty: Number(r.qty), unit: l.unit, reason: r.reason } : null;
    }).filter((x) => x && x.qty > 0);
    if (!items.length) return setError('Add at least one item with a quantity.');
    const over = rows.find((r) => byKey[r.sel] && Number(r.qty) > Number(byKey[r.sel].quantity) + 1e-9);
    if (over) return setError('A quantity exceeds what the branch holds.');
    setBusy(true);
    try {
      await api.post('/transfers/return', { fromLocationId: canPickBranch ? Number(branchId) : undefined, items, note });
      toast.success('Return raised — waiting for the warehouse to process it'); onSaved(); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Return stock to warehouse" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Submitting…' : 'Raise return'}</button></>}>
      {canPickBranch && (
        <div className="field"><label>Returning from branch</label>
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">— select branch —</option>
            {retailLocs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>
      )}
      <table className="grid">
        <thead><tr><th>Item (in branch stock)</th><th style={{ width: 90 }}>Have</th><th style={{ width: 90 }}>Return qty</th><th>Reason</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const l = byKey[r.sel];
            return (
              <tr key={r.key}>
                <td style={{ minWidth: 240 }}>
                  <SearchSelect placeholder="Search item by name or ID…" value={r.sel} onChange={(v) => setRow(r.key, { sel: v })}
                    options={stock.map((s) => ({ value: stockKey(s), label: optLabel(s), sub: `#${s.itemId}`, search: `${s.item} ${s.itemId} ${s.variant || ''}` }))} />
                </td>
                <td className="muted right">{l ? `${num(l.quantity)} ${l.unit}` : '—'}</td>
                <td><input type="number" min="0" step="0.001" max={l ? l.quantity : undefined} value={r.qty} onChange={(e) => setRow(r.key, { qty: e.target.value })} style={{ width: 80 }} /></td>
                <td>
                  <select value={r.reason} onChange={(e) => setRow(r.key, { reason: e.target.value })}>
                    {REASONS.map((x) => <option key={x}>{x}</option>)}
                  </select>
                </td>
                <td><button className="btn btn-outline btn-sm" onClick={() => delRow(r.key)} disabled={rows.length <= 1}>✕</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button className="btn btn-outline btn-sm mt" onClick={addRow}>+ Add item</button>
      {branchId && !stock.length && <p className="muted small">This branch has no product stock to return.</p>}
      <div className="field mt"><label>Note (optional)</label><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Warehouse verifies a return and dispositions each item in one step ──
const ProcessReturnModal = ({ ret, labelFor, readOnly, onClose, onDone }) => {
  const [rows, setRows] = useState(() => (ret.items || []).map((it) => ({
    id: it.id, label: labelFor(it), reason: it.reason, unit: it.unit,
    returned: Number(it.qtyRequested), qty: Number(it.qtyRequested),
    disposition: it.disposition || defaultDispo(it.reason),
    received: Number(it.qtyReceived || 0),
  })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const submit = async () => {
    setError('');
    const items = rows.filter((r) => Number(r.qty) > 0).map((r) => ({ id: r.id, qty: Number(r.qty), disposition: r.disposition }));
    if (!items.length) return setError('Enter quantities to process.');
    setBusy(true);
    try {
      await api.post(`/transfers/${ret.id}/process-return`, { items });
      toast.success('Return processed — stock dispositioned'); onDone(); onClose();
    } catch (e) { setError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`${readOnly ? 'Return' : 'Process return'} ${ret.code}`} onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button>
        {!readOnly && <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Processing…' : 'Verify & process'}</button>}</>}>
      <div className="flex" style={{ justifyContent: 'space-between' }}>
        <div><strong>{ret.fromLocation?.name}</strong> → <strong>{ret.toLocation?.name}</strong></div>
        <span className={`badge ${statusBadge(ret.status)}`}>{statusLabel(ret.status)}</span>
      </div>
      {ret.note && <p className="muted small mt">{ret.note}</p>}
      <div className="table-wrap mt">
        <table className="grid">
          <thead><tr><th>Product</th><th>Reason</th><th className="right">Returned</th>{!readOnly && <th className="right">Verify qty</th>}<th>Disposition</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.label}</td>
                <td className="muted">{r.reason || '—'}</td>
                <td className="right">{num(r.returned)} {r.unit}</td>
                {!readOnly && <td className="right"><input type="number" min="0" step="0.001" max={r.returned} value={r.qty} onChange={(e) => set(r.id, { qty: e.target.value })} style={{ width: 80 }} /></td>}
                <td>
                  {readOnly
                    ? <span className={`badge ${dispoBadge(r.disposition)}`}>{(DISPOSITIONS.find((d) => d.v === r.disposition) || {}).label || r.disposition}</span>
                    : <select value={r.disposition} onChange={(e) => set(r.id, { disposition: e.target.value })}>{DISPOSITIONS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}</select>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!readOnly && <p className="muted small mt">Verify what actually arrived, then pick where each item goes. Reprocess sends it to the kitchen; waste to the damaged/expired location; restock returns it to warehouse stock.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const Returns = () => {
  const { user, can } = useAuth();
  const { locationId: locFilter } = useLocationFilter();
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raising, setRaising] = useState(false);
  const [open, setOpen] = useState(null);

  const canRaise = can('transfers:request') || can('transfers:write');
  const canProcess = can('transfers:write') || can('procurement:write');
  const canPickBranch = can('transfers:write');

  const load = () => {
    setLoading(true);
    api.get('/transfers').then(({ data }) => setReturns((data.transfers || []).filter((t) => t.kind === 'return')))
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get('/products').then(({ data }) => setProducts(data.products || [])).catch(() => {});
    api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {});
  }, []);

  const labelMap = useMemo(() => {
    const m = {};
    for (const p of products) { m[`p${p.id}`] = `${p.name} (${p.unit || 'kg'})`; for (const v of p.variants || []) m[`v${v.id}`] = `${p.name} — ${v.weightLabel}`; }
    return m;
  }, [products]);
  const labelFor = (it) => (it.variantId ? (labelMap[`v${it.variantId}`] || `#${it.itemId}`) : (labelMap[`p${it.itemId}`] || `#${it.itemId}`));

  const shown = returns.filter((t) => !locFilter || String(t.fromLocationId) === String(locFilter) || String(t.toLocationId) === String(locFilter));

  const list = useDataTable(shown, {
    searchPlaceholder: 'Search code, branch, reason…',
    searchKeys: ['code', (t) => t.fromLocation?.name, (t) => (t.items || []).map((i) => i.reason).join(' ')],
    filters: [
      { key: 'status', label: 'Status', value: (t) => statusLabel(t.status) },
      { key: 'from', label: 'Branch', value: (t) => t.fromLocation?.name },
    ],
  });

  return (
    <div>
      <p className="muted small" style={{ marginTop: 0 }}>Branches return stock to the warehouse with a reason. The warehouse verifies each return and records every item as reprocess (→ kitchen), waste (→ damaged) or restock (→ warehouse).</p>
      <TableToolbar dt={list} right={canRaise && <button className="btn btn-primary" onClick={() => setRaising(true)}>+ Return stock</button>} />

      {loading ? <div className="spinner" /> : (<>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Code</th><th>From (branch)</th><th className="right">Products</th><th>Reasons</th><th>Status</th><th className="right">Raised</th><th></th></tr></thead>
            <tbody>
              {list.rows.map((t) => {
                const reasons = [...new Set((t.items || []).map((i) => i.reason).filter(Boolean))];
                const isPending = t.status === 'created';
                const willProcess = isPending && canProcess;
                return (
                  <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setOpen({ ret: t, readOnly: !willProcess })} title={willProcess ? 'Process return' : 'View return'}>
                    <td className="mono">{t.code}</td>
                    <td>{t.fromLocation?.name}</td>
                    <td className="right">{(t.items || []).length}</td>
                    <td className="muted">{reasons.join(', ') || '—'}</td>
                    <td><span className={`badge ${statusBadge(t.status)}`}>{statusLabel(t.status)}</span></td>
                    <td className="muted right">{dt(t.createdAt)}</td>
                    <td className="right muted">{willProcess ? 'Process ›' : 'View ›'}</td>
                  </tr>
                );
              })}
              {list.rows.length === 0 && <tr><td colSpan={7} className="empty">{list.isFiltered ? 'No returns match your search.' : 'No returns yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
        <TablePager dt={list} />
      </>)}

      {raising && <RaiseReturnModal locations={locations} canPickBranch={canPickBranch} myLocationId={user?.locationId} onClose={() => setRaising(false)} onSaved={load} />}
      {open && <ProcessReturnModal ret={open.ret} labelFor={labelFor} readOnly={open.readOnly} onClose={() => setOpen(null)} onDone={load} />}
    </div>
  );
};

export default Returns;
